import { CloseButton, Group, Paper, Stack, Text } from "@mantine/core";
import { IconAlertCircle, IconCircleCheck, IconInfoCircle } from "@tabler/icons-react";
import type * as React from "react";
import { PANEL_PALETTES, PANEL_RADIUS, PANEL_SPACE } from "./tokens";

type Tone = "warning" | "danger" | "info" | "success";

/** Fill and text per tone: the frames' warning is `orange/1` on `orange/8`; the others take
 * the same steps of their palettes. */
const TONE: Record<Tone, { fill: string; text: string; Icon: typeof IconAlertCircle }> = {
  warning: { fill: PANEL_PALETTES.orange[1], text: PANEL_PALETTES.orange[8], Icon: IconAlertCircle },
  danger: { fill: PANEL_PALETTES.red[1], text: PANEL_PALETTES.red[8], Icon: IconAlertCircle },
  info: { fill: PANEL_PALETTES.blue[0], text: PANEL_PALETTES.blue[8], Icon: IconInfoCircle },
  success: { fill: PANEL_PALETTES.green[0], text: PANEL_PALETTES.green[8], Icon: IconCircleCheck },
};

/**
 * A notice band (Figma "Alert"): a tinted fill, a 24px outline icon and 13px text in the
 * tone's dark step. `title` leads in medium weight; `action` sits at the right end;
 * `onClose` adds a close button. Danger and warning notices are announced as alerts.
 */
export function Notice({
  tone,
  title,
  children,
  action,
  onClose,
}: {
  tone: Tone;
  title?: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  onClose?: () => void;
}) {
  const t = TONE[tone];
  return (
    <Paper
      bg={t.fill}
      radius={PANEL_RADIUS.card}
      p={PANEL_SPACE.field}
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
    >
      <Group gap={PANEL_SPACE.field} wrap="nowrap" align="center">
        <t.Icon size={24} stroke={1.1} color={t.text} style={{ flexShrink: 0 }} aria-hidden />
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          {title ? (
            <Text c={t.text} fw={500} lh="normal">
              {title}
            </Text>
          ) : null}
          {children ? (
            <Text component="div" c={t.text} lh="normal">
              {children}
            </Text>
          ) : null}
        </Stack>
        {action}
        {onClose ? <CloseButton size="sm" onClick={onClose} aria-label="Dismiss" c={t.text} /> : null}
      </Group>
    </Paper>
  );
}
